import React, { useState } from "react";
import { Link as RouterLink } from "react-router";

import InputMask from "react-input-mask";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";

import CallModal from "../Modal";
import Button from "components/Button";

import { useAppDispatch } from "store/hooks";
import { preOrderIban } from "store/features/preOrderSlice";

import { routes } from "routes/routes";

import UAFlag from "assets/images/ua_flag-icon.svg";
import IntegrationBg from "assets/images/api_integration-bg.png";
import IntegrationImg from "assets/images/api_integration-img.png";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";
import { FbPixelAnalyticsEvents } from "shared/constants/fb-pixel-analytics";

import { blue600, grey000, grey900 } from "styles";

const APIIntegration: React.FC = () => {
  const dispatch = useAppDispatch();

  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const isMd = useMediaQuery("(max-width: 599px)");
  const isLg = useMediaQuery("(max-width: 1150px)");

  const onChangePhone = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.target.value.length === 18 && setShowTooltip(false);
    setPhoneNumber(event.target.value);
  };

  const onClick = () => {
    if (!phoneNumber || phoneNumber.length < 10) {
      setShowTooltip(true);

      setTimeout(() => {
        setShowTooltip(false);
      }, 3000);
      return;
    }

    setLoading(true);
    const phone = phoneNumber.replace(/\D/g, "");

    dispatch(
      preOrderIban({
        phoneNumber: phone,
        source: "IBAN API інтеграція",
        onSuccess: () => {
          setIsModalOpen(true);
          setLoading(false);
          analytics().sendConversionEvent(FbPixelAnalyticsEvents.Contact, {
            content_name: "IBAN API інтеграція",
          });
          analytics().sendEvent(
            `${AnalyticsEvents.iban_oplata_contact_us}_integration`
          );
        },
        onError: () => {
          setLoading(false);
        },
      })
    );
  };

  const onCloseModal = () => {
    setIsModalOpen(false);
    setPhoneNumber("");
  };

  return (
    <Box sx={styles.installBlock} id="integration">
      <Container sx={styles.container}>
        <Box sx={[styles.textWrapper, isLg && { maxWidth: "100%" }]}>
          <Typography variant="subtitle1" color="grey000" sx={{ mb: 2.5 }}>
            API-Інтеграція
          </Typography>
          <Typography variant="h2" color="grey000" sx={{ mb: 2.5 }}>
            Інтегруйте IBAN oplata API та оптимізуйте платіжні процеси
          </Typography>
          <Typography color="grey000">
            Автоматизуйте генерацію платіжних посилань за допомогою
            API-інтеграції сервісу з вашими CRM та іншими бізнес-платформами.
          </Typography>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="flex-start"
            mt={4}
          >
            <Box sx={styles.inputWrapper}>
              <Box sx={styles.inputImageWrapper}>
                <img src={UAFlag} />
              </Box>
              <Tooltip
                title="Будь ласка, введіть номер телефону"
                open={showTooltip}
                arrow
                disableInteractive
                slotProps={{
                  tooltip: { style: styles.tooltip },
                  arrow: { style: styles.arrow },
                  popper: {
                    modifiers: [
                      { name: "offset", options: { offset: [0, 4] } },
                    ],
                  },
                }}
              >
                <Box sx={{ overflow: "hidden" }}>
                  <InputMask
                    mask="+38 (099) 999-9999"
                    onChange={onChangePhone}
                    value={phoneNumber}
                    maskChar={null}
                  >
                    {(inputMaskProps) => (
                      <TextField
                        {...inputMaskProps}
                        sx={styles.input}
                        variant="standard"
                        placeholder="+38 (000) 000-0000"
                        slotProps={{ input: { disableUnderline: true } }}
                      />
                    )}
                  </InputMask>
                </Box>
              </Tooltip>

              {!isMd && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={onClick}
                  sx={styles.contactBtn}
                  disabled={loading}
                  loading={loading}
                >
                  Замовити інтеграцію
                </Button>
              )}
            </Box>
            {isMd && (
              <Button
                variant="contained"
                color="secondary"
                fullWidth
                onClick={onClick}
                sx={{
                  ...styles.contactBtn,
                  ...{ marginTop: "12px" },
                }}
                disabled={loading}
                loading={loading}
              >
                Замовити інтеграцію
              </Button>
            )}
            <Typography variant="body2" color="grey000" sx={{ mt: 1 }}>
              Продовжуючи, я погоджуюсь з умовами{" "}
              <Link
                component={RouterLink}
                variant="body2"
                to={routes.termsOfService}
                color="grey000"
              >
                Політики конфіденційності
              </Link>
            </Typography>
          </Box>
        </Box>
        <Box sx={[styles.imageWrapper, isLg && { display: "none" }]}>
          <img
            src={IntegrationImg}
            alt="API Integration"
            style={styles.image}
          />
        </Box>
      </Container>
      <CallModal
        isOpen={isModalOpen}
        closeModal={onCloseModal}
        phone={phoneNumber}
      />
    </Box>
  );
};

const styles = {
  installBlock: {
    backgroundImage: `url(${IntegrationBg})`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "cover",
    paddingTop: { xs: "56px", sm: "96px" },
    paddingBottom: { xs: "56px", sm: "96px" },
    textAlign: "left",
    backgroundColor: blue600,
  },
  container: {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    justifyContent: "space-between",
    alignItems: "center",
  },
  textWrapper: {
    maxWidth: "50%",
    marginRight: { xs: 0, md: "20px" },
  },
  inputWrapper: {
    backgroundColor: grey000,
    display: "flex",
    justifyContent: "flex-start",
    alignItems: "center",
    borderRadius: "28px",
    border: `2px solid ${grey000}`,
    padding: "2px",
    width: { xs: "100%", sm: "fit-content" },
  },
  input: {
    marginLeft: "12px",
    minWidth: "210px",
    "& .MuiInputBase-input": {
      fontFamily: "Inter, sans-serif",
      fontSize: 16,
      fontWeight: "400",
      lineHeight: "24px",
      letterSpacing: "2px",
      minWidth: "210px",
    },
  },
  inputImageWrapper: {
    display: "flex",
    alignItems: "center",
    margin: "8px 0 8px 15px",
    paddingRight: "12px",
    borderRight: "1px solid #A6A7AD",
  },
  contactBtn: {
    padding: "12px 20px",
  },
  imageWrapper: {
    minWidth: "45%",
    maxWidth: "50%",
  },
  image: {
    width: "100%",
  },
  tooltip: {
    backgroundColor: grey000,
    color: grey900,
    fontFamily: "Inter, sans-serif",
    fontSize: "12px",
    lineHeight: "130%",
    letterSpacing: "0",
    fontWeight: "500",
    borderRadius: "8px",
    padding: "8px 12px",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.4)",
  },
  arrow: {
    color: grey000,
  },
};

export default APIIntegration;
